/**
 * Landing gear: per-wheel oleo spring/damper with a saturating tyre friction model.
 *
 * ## Why the lateral friction model is the important part
 *
 * An aeroplane on the ground is not a car. A car's tyres are steered and its body follows;
 * an aeroplane's wheels are mostly fixed, its fin is a large weathervane, and the thing that
 * keeps it going where it is pointing is the tyres' refusal to slide sideways. Get the lateral
 * friction wrong and the whole ground handling is wrong in a way that is immediately obvious:
 * the aircraft skates in crosswind, will not weathervane, and slides through turns like a car
 * on ice.
 *
 * So each tyre gets an anisotropic friction model: it rolls almost freely along its own
 * heading (`rollingResistance` ≈ 0.02) and grips hard across it (`lateralFriction` ≈ 0.8).
 * The saturation is `tanh(v/v_ref)` with a small reference speed, which means:
 *
 *   - below the reference speed the tyre behaves like a stiff spring in slip, so the aircraft
 *     sits still on a slope instead of creeping;
 *   - above it the force saturates at `μ·N`, so the tyre can be broken loose and *will* skid
 *     in a hard enough sideways slide;
 *   - it is smooth everywhere, so the 120 Hz integrator never sees a discontinuity.
 *
 * With that in place, weathervaning, differential braking and the crosswind takeoff all emerge
 * from the interaction of the fin, the tyres and the geometry.
 *
 * ## Stability
 *
 * A stiff spring at 120 Hz is a real risk: a 200 kN/m oleo on a 1157 kg aeroplane has a
 * natural period of about 0.5 s, which is fine, but the *friction* can easily be asked for
 * more force than is needed to stop the contact patch inside one tick, and that overshoot
 * oscillates. Each wheel's friction is therefore capped at the impulse that would exactly
 * arrest its share of the aircraft's momentum this tick.
 *
 * `updateFixed(dt)`: yes, at 120 Hz. Allocation-free.
 */

import { Vector3 } from 'three';
import { DEG2RAD, clamp } from '../core/math.js';

export type BrakeChannel = 'left' | 'right' | 'both' | 'none';

export interface WheelConfig {
  name: string;
  /** Contact point with the oleo fully extended, relative to the CG, body axes [m]. */
  positionM: readonly [number, number, number];
  /** Oleo spring rate [N/m]. */
  springRateNPerM: number;
  /** Oleo damping [N·s/m]. */
  dampingNsPerM: number;
  /** Usable travel before the strut bottoms out [m]. */
  maxTravelM: number;
  /** Rolling resistance coefficient along the wheel's heading. */
  rollingResistance: number;
  /** Peak braking friction coefficient. */
  brakeFriction: number;
  /** Peak cornering friction coefficient across the wheel. */
  lateralFriction: number;
  /** Which brake pedal drives this wheel. */
  brakeChannel: BrakeChannel;
  /** Does this wheel steer? */
  steerable?: boolean;
  /** Steering authority at full pedal [deg]. */
  maxSteerAngleDeg?: number;
}

export interface GearConfig {
  wheels: readonly WheelConfig[];
  /**
   * Slip speed at which tyre friction reaches most of its peak [m/s]. Small enough that the
   * aircraft does not creep, large enough that the model stays smooth at 120 Hz.
   */
  frictionReferenceSpeedMps?: number;
}

export interface GearInputs {
  /** Left brake, 0…1. */
  brakeLeft: number;
  /** Right brake, 0…1. */
  brakeRight: number;
  /** Steering command, −1…1 (from the rudder pedals). */
  steer: number;
}

export interface GearOutput {
  /** Is any wheel touching? */
  onGround: boolean;
  /** Total normal force carried by the wheels [N]. */
  weightOnWheelsN: number;
  /** Largest oleo compression across all wheels [m]. */
  maxCompressionM: number;
  /** Vertical speed at the moment of touchdown [m/s], for the touchdown thump. */
  touchdownRateMps: number;
}

export function makeGearOutput(): GearOutput {
  return { onGround: false, weightOnWheelsN: 0, maxCompressionM: 0, touchdownRateMps: 0 };
}

/** Per-wheel state, exposed for the debug HUD and for the tests. */
export interface WheelState {
  compressionM: number;
  normalForceN: number;
  onGround: boolean;
  /** Sideways slip speed at the contact patch [m/s]. */
  lateralSlipMps: number;
  /** True once the lateral force has saturated — the tyre is skidding. */
  skidding: boolean;
}

const wheelBody = /* #__PURE__ */ new Vector3();
const wheelWorld = /* #__PURE__ */ new Vector3();
const contactVelocityWorld = /* #__PURE__ */ new Vector3();
const rotationalVelocityBody = /* #__PURE__ */ new Vector3();
const forceWorld = /* #__PURE__ */ new Vector3();
const forceBody = /* #__PURE__ */ new Vector3();
const forwardWorld = /* #__PURE__ */ new Vector3();

export interface GroundContext {
  /** World position of the CG [m], NED. */
  positionNed: Vector3;
  /** World velocity of the CG [m/s], NED. */
  velocityNed: Vector3;
  /** Body rates [rad/s]. */
  angularVelocityBody: Vector3;
  /** Rotate a body vector into the world. */
  bodyToWorld: (bodyVector: Vector3, out: Vector3) => Vector3;
  /** Rotate a world vector into the body frame. */
  worldToBody: (worldVector: Vector3, out: Vector3) => Vector3;
  /** Ground elevation directly under the aircraft [m MSL]. Flat ground for now. */
  groundElevationM: number;
  /** Aircraft mass [kg], used to cap the friction impulse. */
  massKg: number;
}

export class LandingGear {
  private readonly wheels: readonly WheelConfig[];
  private readonly frictionReferenceSpeedMps: number;
  readonly states: WheelState[];
  readonly output = makeGearOutput();

  private wasOnGround = false;

  constructor(config: GearConfig) {
    this.wheels = config.wheels;
    this.frictionReferenceSpeedMps = config.frictionReferenceSpeedMps ?? 0.6;
    this.states = config.wheels.map(() => ({
      compressionM: 0,
      normalForceN: 0,
      onGround: false,
      lateralSlipMps: 0,
      skidding: false,
    }));
  }

  /**
   * Accumulate the ground reaction into the body-axis force and moment.
   * Allocation-free.
   */
  accumulate(
    context: GroundContext,
    inputs: GearInputs,
    dtSeconds: number,
    forceAccumulator: Vector3,
    momentAccumulator: Vector3,
  ): GearOutput {
    const out = this.output;
    out.onGround = false;
    out.weightOnWheelsN = 0;
    out.maxCompressionM = 0;
    out.touchdownRateMps = 0;

    // Share the friction impulse cap between however many wheels are down, so two wheels do
    // not each try to arrest the whole aeroplane.
    const massPerWheel = context.massKg / Math.max(1, this.wheels.length);

    for (let i = 0; i < this.wheels.length; i++) {
      const wheel = this.wheels[i];
      const state = this.states[i];

      wheelBody.set(wheel.positionM[0], wheel.positionM[1], wheel.positionM[2]);
      context.bodyToWorld(wheelBody, wheelWorld);
      wheelWorld.add(context.positionNed);

      // NED: altitude is −z, so the contact point is below ground when −z < elevation.
      const wheelAltitude = -wheelWorld.z;
      const compression = context.groundElevationM - wheelAltitude;

      if (compression <= 0) {
        state.compressionM = 0;
        state.normalForceN = 0;
        state.onGround = false;
        state.lateralSlipMps = 0;
        state.skidding = false;
        continue;
      }

      // --- contact point velocity ---------------------------------------------------
      // ω × r written out in scalars: going through Vector3.cross costs a call whose double
      // results V8 sometimes boxes, and with three wheels a tick that was 240 B of garbage
      // every 8 ms.
      const omega = context.angularVelocityBody;
      rotationalVelocityBody.set(
        omega.y * wheelBody.z - omega.z * wheelBody.y,
        omega.z * wheelBody.x - omega.x * wheelBody.z,
        omega.x * wheelBody.y - omega.y * wheelBody.x,
      );
      context.bodyToWorld(rotationalVelocityBody, contactVelocityWorld);
      contactVelocityWorld.x += context.velocityNed.x;
      contactVelocityWorld.y += context.velocityNed.y;
      contactVelocityWorld.z += context.velocityNed.z;

      // --- oleo ---------------------------------------------------------------------
      // Positive compression rate means the strut is being squashed (moving down, +z).
      const compressionRate = contactVelocityWorld.z;
      const travel = Math.min(compression, wheel.maxTravelM);
      // Past full travel the strut bottoms out on a much stiffer stop.
      const bottomedOut = Math.max(0, compression - wheel.maxTravelM);
      const springForce =
        travel * wheel.springRateNPerM + bottomedOut * wheel.springRateNPerM * 12;
      // Damping only resists compression as strongly as it resists extension would make the
      // gear sticky, so damping is applied symmetrically but the total is clamped positive:
      // a strut can push, never pull.
      const normalForce = Math.max(0, springForce + compressionRate * wheel.dampingNsPerM);

      if (!this.wasOnGround && normalForce > 0) {
        out.touchdownRateMps = Math.max(out.touchdownRateMps, compressionRate);
      }

      // --- tyre friction --------------------------------------------------------------
      // The wheel's heading: body x, steered, projected onto the ground plane.
      const steerAngle =
        wheel.steerable === true
          ? clamp(inputs.steer, -1, 1) * (wheel.maxSteerAngleDeg ?? 0) * DEG2RAD
          : 0;
      wheelBody.set(Math.cos(steerAngle), Math.sin(steerAngle), 0);
      context.bodyToWorld(wheelBody, forwardWorld);
      forwardWorld.z = 0;
      const forwardLength = Math.hypot(forwardWorld.x, forwardWorld.y);
      if (forwardLength > 1e-6) {
        forwardWorld.x /= forwardLength;
        forwardWorld.y /= forwardLength;
      } else {
        forwardWorld.set(1, 0, 0);
      }

      // Split the horizontal contact velocity along and across the wheel.
      const alongSpeed = contactVelocityWorld.x * forwardWorld.x + contactVelocityWorld.y * forwardWorld.y;
      // Left-hand normal in the ground plane (rotate forward by +90° about the down axis).
      const lateralX = -forwardWorld.y;
      const lateralY = forwardWorld.x;
      const lateralSpeed = contactVelocityWorld.x * lateralX + contactVelocityWorld.y * lateralY;

      const brake = brakeCommand(wheel.brakeChannel, inputs);
      const longitudinalCoefficient = wheel.rollingResistance + brake * wheel.brakeFriction;

      const reference = this.frictionReferenceSpeedMps;
      let longitudinalForce = -normalForce * longitudinalCoefficient * Math.tanh(alongSpeed / reference);
      let lateralForce = -normalForce * wheel.lateralFriction * Math.tanh(lateralSpeed / reference);

      // Cap each component at the impulse that would just arrest this wheel's share of the
      // momentum. Without this a stiff tyre overshoots inside one tick and buzzes.
      const maxLongitudinal = (massPerWheel * Math.abs(alongSpeed)) / dtSeconds;
      const maxLateral = (massPerWheel * Math.abs(lateralSpeed)) / dtSeconds;
      longitudinalForce = clamp(longitudinalForce, -maxLongitudinal, maxLongitudinal);
      lateralForce = clamp(lateralForce, -maxLateral, maxLateral);

      // --- assemble ---------------------------------------------------------------------
      forceWorld.set(
        forwardWorld.x * longitudinalForce + lateralX * lateralForce,
        forwardWorld.y * longitudinalForce + lateralY * lateralForce,
        -normalForce, // up is −z
      );
      context.worldToBody(forceWorld, forceBody);
      forceAccumulator.x += forceBody.x;
      forceAccumulator.y += forceBody.y;
      forceAccumulator.z += forceBody.z;

      wheelBody.set(wheel.positionM[0], wheel.positionM[1], wheel.positionM[2]);
      momentAccumulator.x += wheelBody.y * forceBody.z - wheelBody.z * forceBody.y;
      momentAccumulator.y += wheelBody.z * forceBody.x - wheelBody.x * forceBody.z;
      momentAccumulator.z += wheelBody.x * forceBody.y - wheelBody.y * forceBody.x;

      // --- state ---------------------------------------------------------------------------
      state.compressionM = compression;
      state.normalForceN = normalForce;
      state.onGround = true;
      state.lateralSlipMps = lateralSpeed;
      state.skidding = Math.abs(lateralSpeed) > reference * 2;

      out.onGround = true;
      out.weightOnWheelsN += normalForce;
      if (compression > out.maxCompressionM) out.maxCompressionM = compression;
    }

    this.wasOnGround = out.onGround;
    return out;
  }

  /**
   * The gear as it is when it is in the wells: no contact, no load, no touchdown edge.
   *
   * Returned instead of running the contact model when the undercarriage is not down and locked.
   * It also clears `wasOnGround`, which matters: without that, retracting after take-off and
   * lowering again on approach would leave the model believing it had never left the ground, and
   * the touchdown thump — which fires on the *edge* — would never sound again.
   */
  retracted(): GearOutput {
    const out = this.output;
    out.onGround = false;
    out.weightOnWheelsN = 0;
    out.maxCompressionM = 0;
    out.touchdownRateMps = 0;
    this.wasOnGround = false;
    for (const state of this.states) {
      state.compressionM = 0;
      state.normalForceN = 0;
      state.onGround = false;
      state.lateralSlipMps = 0;
      state.skidding = false;
    }
    return out;
  }

  /** Forget the touchdown edge, e.g. after a respawn. */
  reset(): void {
    this.wasOnGround = false;
    for (const state of this.states) {
      state.compressionM = 0;
      state.normalForceN = 0;
      state.onGround = false;
      state.lateralSlipMps = 0;
      state.skidding = false;
    }
  }

  /**
   * Height of the lowest wheel below the CG with the oleos extended [m]. The spawn code uses
   * it to place the aircraft with its wheels exactly on the ground.
   */
  get lowestWheelOffsetM(): number {
    let lowest = 0;
    for (const wheel of this.wheels) {
      if (wheel.positionM[2] > lowest) lowest = wheel.positionM[2];
    }
    return lowest;
  }
}

function brakeCommand(channel: BrakeChannel, inputs: GearInputs): number {
  switch (channel) {
    case 'left':
      return clamp(inputs.brakeLeft, 0, 1);
    case 'right':
      return clamp(inputs.brakeRight, 0, 1);
    case 'both':
      return clamp(Math.max(inputs.brakeLeft, inputs.brakeRight), 0, 1);
    case 'none':
      return 0;
  }
}
